import classNames from "classnames";
import React from "react";
import { Link as RLink } from "react-router-dom";

import { ConfigData } from "../../config";
import { Messages } from "../../context/MessagesContext";
import { formatRelease, toQuarter } from "../../date";
import { useFetch } from "../../hooks/use-fetch";
import { Item } from "../../model";
import {
  RADARS,
  RADAR_SLUGS,
  RadarDescriptor,
  RadarSlug,
  radarDataUrl,
} from "../../radars";
import {
  initialRelease,
  itemsAtRelease,
  latestRelease,
  releaseSummary,
} from "../../releases";
import SiteFooter from "../SiteFooter/SiteFooter";
import SiteHeader, { SiteHeaderCta } from "../SiteHeader/SiteHeader";
import RadarMiniature from "./RadarMiniature";
import "./radars-home.scss";

type RadarData = {
  items: Item[];
  releases: string[];
};

/**
 * Everything the home page says about one radar, read from that radar's own
 * `rd.json` and `config.json`. No figure on this page is written by hand: the
 * counts, the quarters and the segment names all come from the published data,
 * so an edition cannot be published behind a stale landing page.
 */
type Snapshot = {
  descriptor: RadarDescriptor;
  config?: ConfigData;
  releases: string[];
  latest?: string;
  /** Every entry with its full revision history, for the archive counts. */
  all: Item[];
  /** Entries as they stand at the latest edition, for the preview and counts. */
  items: Item[];
  /** Entries that appeared or moved at the latest edition. */
  moved: number;
  loaded: boolean;
};

const useSnapshot = (slug: RadarSlug): Snapshot => {
  const buildHash = process.env.REACT_APP_BUILDHASH;
  const data = useFetch<RadarData>(radarDataUrl(slug, `rd.json?${buildHash}`));
  const config = useFetch<ConfigData>(
    radarDataUrl(slug, `config.json?${buildHash}`)
  );

  return React.useMemo(() => {
    const releases = data?.releases ?? [];
    const latest = latestRelease(releases);
    const summary =
      data && latest
        ? releaseSummary(data.items, latest, releases)
        : { added: [], changed: [], total: 0 };

    return {
      descriptor: RADARS[slug],
      config,
      releases,
      latest,
      all: data?.items ?? [],
      items: data && latest ? itemsAtRelease(data.items, latest, releases) : [],
      moved: summary.added.length + summary.changed.length,
      loaded: Boolean(data && config),
    };
  }, [data, config, slug]);
};

const Stat = ({
  value,
  label,
  small,
}: {
  value: React.ReactNode;
  label: string;
  small?: boolean;
}) => (
  <div
    className={classNames("radars-home__stat", {
      "radars-home__stat--small": small,
    })}
  >
    <span className="radars-home__stat-value">{value}</span>
    <span className="radars-home__stat-label">{label}</span>
  </div>
);

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="radars-home__eyebrow">{children}</div>
);

const Arrow = () => (
  <svg
    className="radars-home__arrow"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

/** One radar, presented as a full width row rather than a card. */
const RadarRow = ({
  snapshot,
  index,
}: {
  snapshot: Snapshot;
  index: number;
}) => {
  const { descriptor, config, items, moved, latest, loaded } = snapshot;
  const segments = config ? Object.values(config.quadrants) : [];

  return (
    <RLink
      className="radars-home__radar"
      to={`/${descriptor.slug}/index.html`}
      style={{
        // Drives the ordinal colour and the border the row grows on hover.
        ["--radar-accent" as string]: descriptor.accent,
        animationDelay: `${140 + index * 90}ms`,
      }}
    >
      <div className="radars-home__radar-body">
        <div className="radars-home__radar-heading">
          <span className="radars-home__radar-ordinal">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h2 className="radars-home__radar-title">{descriptor.shortName}</h2>
        </div>
        <p className="radars-home__radar-tagline">{descriptor.tagline}</p>

        {segments.length > 0 && (
          <ul className="radars-home__chips">
            {segments.map((segment) => (
              <li className="radars-home__chip" key={segment}>
                {segment}
              </li>
            ))}
          </ul>
        )}

        <div className="radars-home__stats radars-home__stats--radar">
          <Stat
            small
            value={loaded ? items.length : "—"}
            label="Technologies"
          />
          <Stat small value={loaded ? moved : "—"} label="Moved this quarter" />
          <Stat
            small
            value={latest ? toQuarter(latest) : "—"}
            label="Edition"
          />
        </div>

        <span className="radars-home__radar-cta">
          Open the {descriptor.shortName}
          <Arrow />
        </span>
      </div>

      {config && (
        <RadarMiniature
          className="radars-home__radar-preview"
          items={items}
          config={config}
        />
      )}
    </RLink>
  );
};

const METHOD_STEPS = [
  {
    title: "Propose",
    body: "Any engineer opens a blip with the project it was used on, the problem it solved, and what it cost us.",
  },
  {
    title: "Review",
    body: "The radar board — engineers from the AI, Data and Platform teams — reads the evidence and challenges it.",
  },
  {
    title: "Place",
    body: "Adopt requires two independent production uses. Hold requires a written reason. No blip moves without a majority.",
  },
  {
    title: "Publish",
    body: "A new edition every quarter. Previous editions stay online, unedited, so the changes of mind remain visible.",
  },
];

/** Ring colours, innermost first, matching the chart legend. */
const RING_COLOURS = [
  "var(--ring-adopt)",
  "var(--ring-trial)",
  "var(--ring-assess)",
  "var(--ring-hold)",
];

type Edition = {
  release: string;
  quarter: string;
  published: string;
  counts: Partial<Record<RadarSlug, number>>;
  current: boolean;
};

/**
 * One row per published edition, both radars side by side. Built from the union
 * of the two release lists so that a radar which starts later simply has no
 * figure in the earlier rows.
 */
const useEditions = (snapshots: Record<RadarSlug, Snapshot>): Edition[] =>
  React.useMemo(() => {
    const releases = Array.from(
      new Set(RADAR_SLUGS.flatMap((slug) => snapshots[slug].releases))
    ).sort();
    const newest = latestRelease(releases);

    return releases
      .slice()
      .reverse()
      .map((release) => ({
        release,
        quarter: toQuarter(release),
        published: formatRelease(release),
        current: release === newest,
        counts: RADAR_SLUGS.reduce<Partial<Record<RadarSlug, number>>>(
          (counts, slug) => {
            const { releases: own, all } = snapshots[slug];
            if (own.includes(release)) {
              counts[slug] = itemsAtRelease(all, release, own).length;
            }
            return counts;
          },
          {}
        ),
      }));
  }, [snapshots]);

export default function RadarsHome() {
  const tech = useSnapshot("tech");
  const ai = useSnapshot("ai");
  const snapshots: Record<RadarSlug, Snapshot> = React.useMemo(
    () => ({ tech, ai }),
    [tech, ai]
  );
  const editions = useEditions(snapshots);

  // The ring definitions are published with the radars themselves, so reading
  // them keeps this page and the "How to use" page from drifting apart.
  const rings = useFetch<Messages>(
    radarDataUrl("tech", `messages.json?${process.env.REACT_APP_BUILDHASH}`)
  )?.pageHelp?.rings;

  React.useEffect(() => {
    document.title = "Elyadata Radars";
  }, []);

  const loaded = tech.loaded && ai.loaded;
  const total = tech.items.length + ai.items.length;
  const latest = tech.latest ?? ai.latest;
  const first = initialRelease(tech.releases);

  return (
    <div className="radars-home">
      <SiteHeader
        nav={
          <>
            <a className="site-header__link" href="#radars">
              Radars
            </a>
            <a className="site-header__link" href="#method">
              Method
            </a>
            <a className="site-header__link" href="#archive">
              Archive
            </a>
          </>
        }
        controls={
          <SiteHeaderCta href="https://www.elyadata.com/">
            elyadata.com
          </SiteHeaderCta>
        }
      />

      <main className="site-shell">
        <section className="radars-home__hero" id="top">
          <svg
            className="radars-home__hero-mark"
            viewBox="0 0 400 560"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="200" cy="195" r="170" fill="var(--logo-cyan)" />
            <circle cx="200" cy="365" r="170" fill="var(--logo-magenta)" />
            <path
              d="M52.8 280 A170 170 0 0 1 347.2 280 A170 170 0 0 1 52.8 280 Z"
              fill="var(--logo-deepblue)"
            />
          </svg>

          <div className="radars-home__hero-body">
            <Eyebrow>Elyadata Engineering · Updated quarterly</Eyebrow>
            <h1 className="radars-home__title">
              A written record of what
              <br />
              we actually build with.
            </h1>
            <p className="radars-home__lead">
              Two radars, maintained by the engineers on our delivery teams.
              Every technology listed here has been placed by evidence from real
              projects — not by preference, not by vendor briefing. Read them to
              see how Elyadata builds, and where we are heading next.
            </p>

            <div className="radars-home__stats">
              <Stat value={loaded ? total : "—"} label="Technologies tracked" />
              <Stat
                value={latest ? toQuarter(latest) : "—"}
                label="Latest edition"
              />
              <Stat
                value={tech.releases.length || "—"}
                label="Quarterly editions"
              />
              <Stat
                value={first ? toQuarter(first) : "—"}
                label="Tracking since"
              />
            </div>
          </div>
        </section>

        <section className="radars-home__radars" id="radars">
          {RADAR_SLUGS.map((slug, index) => (
            <RadarRow key={slug} snapshot={snapshots[slug]} index={index} />
          ))}
        </section>

        <section className="radars-home__method" id="method">
          <div className="radars-home__method-inner">
            <div className="radars-home__method-grid">
              <div>
                <Eyebrow>Method &amp; governance</Eyebrow>
                <h2 className="radars-home__section-title">
                  Placement is an argument, not an opinion.
                </h2>
                <p className="radars-home__section-lead">
                  A technology enters the radar when an Elyadata team has used
                  it on a real engagement and can describe what happened. Ring
                  changes are decided in a quarterly review, in the open, with
                  the project evidence on the table.
                </p>
              </div>

              <ol className="radars-home__steps">
                {METHOD_STEPS.map((step, index) => (
                  <li className="radars-home__step" key={step.title}>
                    <span className="radars-home__step-ordinal">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="radars-home__step-title">{step.title}</h3>
                      <p className="radars-home__step-body">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {rings && (
              <dl className="radars-home__rings">
                {rings.map((ring, index) => (
                  <div className="radars-home__ring" key={ring.name}>
                    <dt className="radars-home__ring-name">
                      <span
                        className="radars-home__ring-dot"
                        style={{
                          background:
                            RING_COLOURS[
                              Math.min(index, RING_COLOURS.length - 1)
                            ],
                        }}
                      />
                      {ring.name}
                    </dt>
                    <dd className="radars-home__ring-body">
                      {ring.description}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </section>

        <section className="radars-home__archive" id="archive">
          <div className="radars-home__archive-head">
            <div>
              <Eyebrow>Archive</Eyebrow>
              <h2 className="radars-home__section-title">
                Every edition, still readable.
              </h2>
            </div>
            <p className="radars-home__archive-note">
              Past editions are never rewritten. The movement between them is
              the interesting part.
            </p>
          </div>

          <div className="radars-home__table-scroll">
            <table className="radars-home__table">
              <thead>
                <tr>
                  <th scope="col">Edition</th>
                  <th scope="col">Tech radar</th>
                  <th scope="col">AI radar</th>
                  <th scope="col">Published</th>
                  <th scope="col">
                    <span className="radars-home__sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {editions.map((edition) => (
                  <tr key={edition.release}>
                    <th scope="row" className="radars-home__cell-quarter">
                      {edition.quarter}
                    </th>
                    {RADAR_SLUGS.map((slug) => (
                      <td className="radars-home__cell-count" key={slug}>
                        {typeof edition.counts[slug] === "number" ? (
                          <RLink
                            className="radars-home__cell-link"
                            to={`/${slug}/index.html?release=${edition.release}`}
                          >
                            {edition.counts[slug]} technologies
                            <Arrow />
                          </RLink>
                        ) : (
                          "—"
                        )}
                      </td>
                    ))}
                    <td className="radars-home__cell-date">
                      {edition.published}
                    </td>
                    <td className="radars-home__cell-tag">
                      <span
                        className={classNames("radars-home__tag", {
                          "radars-home__tag--current": edition.current,
                        })}
                      >
                        {edition.current ? "Current" : "Archived"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <SiteFooter latestQuarter={latest ? toQuarter(latest) : undefined} />
    </div>
  );
}
